import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { CustomInput } from '../components/CustomInput';
import { CustomPicker } from '../components/CustomPicker';
import { DatePickerInput } from '../components/DatePickerInput';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { theme } from '../constants/theme';
import ApiService from '../services/ApiService';
import { Cow, CowRecord, RecordType } from '../models/types';
import { generateId, getISODateString, numericValidation, decimalValidation, alphabetOnlyWithSpace, alphanumericWithoutSpace, alphanumericWithSpace, parseMilkQuantityToLiters } from '../utils/helpers';
import { ScreenHeader } from '../components/ScreenHeader';

interface CowRecordFormData {
    date: string;
    fodderQuantity: string;
    fodderCost: string;
    fodderType: string;
    medicineName: string;
    medicineCost: string;
    medicineNotes: string;
    milkQuantity: string;
    milkTime: 'morning' | 'evening';
    fatContent: string;
    milkPrice: string;
    notes: string;
}

const AddCowRecordScreen = ({ navigation, route }: any) => {
    const cow = route.params?.cow as Cow;
    const existingRecord = route.params?.existingRecord as CowRecord | undefined;
    const defaultRecordType = route.params?.recordType as RecordType | undefined;
    const isEditMode = !!existingRecord;

    const [loading, setLoading] = useState(false);
    const [recordType, setRecordType] = useState<RecordType>(existingRecord?.recordType || defaultRecordType || 'milk');

    // Determine default milk time based on current hour
    const getDefaultMilkTime = (): 'morning' | 'evening' => {
        const currentHour = new Date().getHours();
        return currentHour < 12 ? 'morning' : 'evening';
    };

    const { control, handleSubmit, formState: { errors } } = useForm<CowRecordFormData>({
        defaultValues: {
            date: existingRecord?.date || getISODateString(new Date()),
            fodderQuantity: existingRecord?.fodderQuantity?.toString() || '',
            fodderCost: existingRecord?.fodderCost?.toString() || '',
            fodderType: existingRecord?.fodderType || '',
            medicineName: existingRecord?.medicineName || '',
            medicineCost: existingRecord?.medicineCost?.toString() || '',
            medicineNotes: existingRecord?.medicineNotes || '',
            milkQuantity: existingRecord?.milkQuantity?.toString() || '',
            milkTime: existingRecord?.milkTime || getDefaultMilkTime(),
            fatContent: existingRecord?.fatContent?.toString() || '',
            milkPrice: existingRecord?.milkPrice?.toString() || '',
            notes: existingRecord?.notes || '',
        },
        mode: 'onChange',
    });

    // Live watchers for real-time total calculation
    const watchedQuantity = useWatch({ control, name: 'milkQuantity' });
    const watchedPrice = useWatch({ control, name: 'milkPrice' });
    const liveTotal = (() => {
        const q = parseMilkQuantityToLiters(watchedQuantity || '');
        const p = parseFloat(watchedPrice);
        return (!isNaN(q) && !isNaN(p) && q > 0 && p > 0) ? q * p : null;
    })();

    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        onConfirm?: () => void;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const onSubmit = useCallback(async (data: CowRecordFormData) => {
        setLoading(true);
        try {
            const milkQty = parseMilkQuantityToLiters(data.milkQuantity);

            const record: CowRecord = {
                id: existingRecord?.id || generateId('REC'),
                cowId: cow.id,
                recordType,
                date: data.date,
                createdAt: existingRecord?.createdAt || new Date().toISOString(),
                ...(recordType === 'fodder' && {
                    fodderQuantity: parseFloat(data.fodderQuantity),
                    fodderCost: data.fodderCost
                        ? parseFloat(data.fodderCost)
                        : undefined,
                    fodderType: data.fodderType || undefined,
                }),
                ...(recordType === 'medicine' && {
                    medicineName: data.medicineName,
                    medicineCost: data.medicineCost
                        ? parseFloat(data.medicineCost)
                        : undefined,
                    medicineNotes: data.medicineNotes || undefined,
                }),
                ...(recordType === 'milk' && {
                    milkQuantity: milkQty,
                    milkTime: data.milkTime,
                    fatContent: data.fatContent
                        ? parseFloat(data.fatContent)
                        : undefined,
                    milkPrice: data.milkPrice
                        ? parseFloat(data.milkPrice)
                        : undefined,
                    milkTotal: (data.milkPrice && data.milkQuantity)
                        ? milkQty * parseFloat(data.milkPrice)
                        : undefined,
                }),
                notes: data.notes || undefined,
            };

            if (isEditMode && existingRecord) {
                await ApiService.updateCowRecord(existingRecord.id, record);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Record updated successfully',
                    onConfirm: () => {
                        setModal(prev => ({ ...prev, visible: false }));
                        navigation.goBack();
                    },
                });
            } else {
                await ApiService.addCowRecord(record);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Record added successfully',
                    onConfirm: () => {
                        setModal(prev => ({ ...prev, visible: false }));
                        navigation.goBack();
                    },
                });
            }
        } catch (error) {
            console.error('Error saving record:', error);
            setModal({ visible: true, type: 'error', title: 'Error', message: 'Failed to save record. Please try again.' });
        } finally {
            setLoading(false);
        }
    }, [existingRecord, cow.id, recordType, isEditMode, navigation]);

    const renderFodderForm = () => (
        <>
            <Controller
                control={control}
                name="fodderQuantity"
                rules={{ required: recordType === 'fodder' ? 'Fodder quantity is required' : false }}
                render={({ field: { onChange, onBlur, value } }) => (
                    <CustomInput
                        label="Quantity (kg) *"
                        value={value}
                        onChangeText={(text) => onChange(decimalValidation(text))}
                        onBlur={onBlur}
                        icon="weight-kilogram"
                        error={errors.fodderQuantity?.message}
                        keyboardType="decimal-pad"
                        placeholder="e.g., 25"
                    />
                )}
            />
            <Controller
                control={control}
                name="fodderCost"
                rules={{ required: recordType === 'fodder' ? 'Cost is required' : false }}
                render={({ field: { onChange, onBlur, value } }) => (
                    <CustomInput
                        label="Cost *"
                        value={value}
                        onChangeText={(text) => onChange(decimalValidation(text))}
                        onBlur={onBlur}
                        icon="currency-inr"
                        error={errors.fodderCost?.message}
                        keyboardType="decimal-pad"
                        placeholder="e.g., 500"
                    />
                )}
            />
            <Controller
                control={control}
                name="fodderType"
                render={({ field: { onChange, onBlur, value } }) => (
                    <CustomInput
                        label="Type (Optional)"
                        value={value}
                        onChangeText={(text) => onChange(alphabetOnlyWithSpace(text))}
                        onBlur={onBlur}
                        icon="grass"
                        placeholder="e.g., Green grass, Dry hay"
                    />
                )}
            />
        </>
    );

    const renderMedicineForm = () => (
        <>
            <Controller
                control={control}
                name="medicineName"
                rules={{ required: recordType === 'medicine' ? 'Medicine name is required' : false }}
                render={({ field: { onChange, onBlur, value } }) => (
                    <CustomInput
                        label="Medicine Name *"
                        value={value}
                        onChangeText={(text) => onChange(alphanumericWithSpace(text))}
                        onBlur={onBlur}
                        icon="medical-bag"
                        error={errors.medicineName?.message}
                        placeholder="e.g., Antibiotic"
                    />
                )}
            />
            <Controller
                control={control}
                name="medicineCost"
                rules={{ required: recordType === 'medicine' ? 'Cost is required' : false }}
                render={({ field: { onChange, onBlur, value } }) => (
                    <CustomInput
                        label="Cost *"
                        value={value}
                        onChangeText={(text) => onChange(decimalValidation(text))}
                        onBlur={onBlur}
                        icon="currency-inr"
                        error={errors.medicineCost?.message}
                        keyboardType="decimal-pad"
                        placeholder="e.g., 350"
                    />
                )}
            />
            <Controller
                control={control}
                name="medicineNotes"
                render={({ field: { onChange, onBlur, value } }) => (
                    <CustomInput
                        label="Medicine Notes (Optional)"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        icon="note-text"
                        placeholder="Dosage, frequency, etc."
                        multiline
                        numberOfLines={3}
                    />
                )}
            />
        </>
    );

    const renderMilkForm = () => (
        <>
            <Controller
                control={control}
                name="milkQuantity"
                rules={{ required: recordType === 'milk' ? 'Milk quantity is required' : false }}
                render={({ field: { onChange, onBlur, value } }) => (
                    <CustomInput
                        label="Quantity (L.GGG) *"
                        value={value}
                        onChangeText={(text) => onChange(decimalValidation(text))}
                        onBlur={onBlur}
                        icon="cup"
                        error={errors.milkQuantity?.message}
                        keyboardType="decimal-pad"
                        placeholder="e.g., 1.100"
                    />
                )}
            />
            <Text style={styles.formatHint}>Format: L.GGG (e.g., 1.100 = 1 L 100 g)</Text>

            <Controller
                control={control}
                name="milkTime"
                render={({ field: { onChange, value } }) => (
                    <CustomPicker
                        label="Milking Time *"
                        value={value}
                        onValueChange={onChange}
                        icon="clock-outline"
                        options={[
                            { label: 'Morning', value: 'morning' },
                            { label: 'Evening', value: 'evening' },
                        ]}
                    />
                )}
            />

            <Controller
                control={control}
                name="fatContent"
                render={({ field: { onChange, onBlur, value } }) => (
                    <CustomInput
                        label="Fat Content % (Optional)"
                        value={value}
                        onChangeText={(text) => onChange(decimalValidation(text))}
                        onBlur={onBlur}
                        icon="water-percent"
                        keyboardType="decimal-pad"
                        placeholder="e.g., 4 or 5.5"
                    />
                )}
            />

            <Controller
                control={control}
                name="milkPrice"
                render={({ field: { onChange, onBlur, value } }) => (
                    <CustomInput
                        label="Price per Liter (Optional)"
                        value={value}
                        onChangeText={(text) => onChange(decimalValidation(text))}
                        onBlur={onBlur}
                        icon="currency-inr"
                        keyboardType="decimal-pad"
                        placeholder="e.g., 50"
                    />
                )}
            />

            {/* Live Total Display */}
            {liveTotal !== null && (
                <View style={styles.totalCard}>
                    <View style={styles.totalCardLeft}>
                        <Icon name="calculator" size={18} color={theme.colors.primary} />
                        <Text style={styles.totalCardLabel}>Total :</Text>
                    </View>
                    <Text style={styles.totalCardValue}>₹{liveTotal.toFixed(2)}</Text>
                </View>
            )}
        </>
    );

    return (
        <SafeAreaView style={styles.container}>
            <ScreenHeader
                title="Add Cow Record"
                navigation={navigation}
                showBackButton={true}
            />
            <KeyboardAwareScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.contentContainer}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >
                <View style={styles.form}>
                    {/* Cow Info Header */}
                    <View style={styles.cowInfoCard}>
                        <View style={styles.cowInfoIcon}>
                            <Icon name="cow" size={24} color={theme.colors.primary} />
                        </View>
                        <View style={styles.cowInfoContent}>
                            <Text style={styles.cowInfoLabel}>Adding record for</Text>
                            <Text style={styles.cowInfoValue}>
                                {cow.id} {cow.name ? `• ${cow.name}` : ''}
                            </Text>
                        </View>
                    </View>

                    <View style={styles.typeSelector}>
                        {['milk', 'fodder', 'medicine'].map(type => (
                            <TouchableOpacity
                                key={type}
                                style={[
                                    styles.typeButton,
                                    recordType === type && styles.typeButton_active,
                                ]}
                                onPress={() => setRecordType(type as RecordType)}>
                                <Text
                                    style={[
                                        styles.typeButtonText,
                                        recordType === type && styles.typeButtonText_active,
                                    ]}>
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                </Text>
                            </TouchableOpacity>
                        ))}
                    </View>

                    <Controller
                        control={control}
                        name="date"
                        rules={{ required: 'Date is required' }}
                        render={({ field: { onChange, value } }) => (
                            <DatePickerInput
                                label="Date *"
                                value={value}
                                onChangeDate={onChange}
                                error={errors.date?.message}
                                placeholder="Select date"
                                maximumDate={new Date()}
                            />
                        )}
                    />

                    {recordType === 'milk' && renderMilkForm()}
                    {recordType === 'fodder' && renderFodderForm()}
                    {recordType === 'medicine' && renderMedicineForm()}

                    <Controller
                        control={control}
                        name="notes"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Additional Notes (Optional)"
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                icon="note-text"
                                placeholder="Any additional information..."
                                multiline
                                numberOfLines={3}
                            />
                        )}
                    />

                    {/* Action Buttons */}
                    <View style={styles.buttonRow}>
                        <TouchableOpacity
                            style={styles.cancelBtn}
                            onPress={() => navigation.goBack()}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.saveBtn}
                            onPress={handleSubmit(onSubmit)}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.saveBtnText}>Save Record</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
                onConfirm={modal.onConfirm}
            />

            <LoadingOverlay visible={loading} />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollView: {
        flex: 1,
    },
    contentContainer: {
        flexGrow: 1,
    },
    form: {
        padding: theme.spacing.lg,
    },
    formatHint: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: -theme.spacing.xs,
        marginBottom: theme.spacing.md,
    },
    cowInfoCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        padding: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        marginBottom: theme.spacing.lg,
        ...theme.shadows.sm,
    },
    cowInfoIcon: {
        width: 48,
        height: 48,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.primary + '15',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    cowInfoContent: {
        flex: 1,
    },
    cowInfoLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    cowInfoValue: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
        marginTop: 2,
    },
    typeSelector: {
        flexDirection: 'row',
        marginBottom: theme.spacing.lg,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.xs,
    },
    typeButton: {
        flex: 1,
        paddingVertical: theme.spacing.sm,
        alignItems: 'center',
        borderRadius: theme.borderRadius.sm,
    },
    typeButton_active: {
        backgroundColor: theme.colors.primary,
    },
    typeButtonText: {
        ...theme.typography.body2,
        color: theme.colors.text,
        fontWeight: '600',
    },
    typeButtonText_active: {
        color: theme.colors.surface,
    },
    buttonRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.md,
    },
    cancelBtn: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    cancelBtnText: {
        ...theme.typography.button,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    saveBtn: {
        flex: 2,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.primary,
        ...theme.shadows.sm,
    },
    saveBtnText: {
        ...theme.typography.button,
        color: '#FFFFFF',
        fontWeight: '700',
    },
    totalCard: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: theme.colors.primary + '12',
        borderRadius: theme.borderRadius.md,
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        borderWidth: 1,
        borderColor: theme.colors.primary + '30',
        marginTop: theme.spacing.xs,
    },
    totalCardLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
    },
    totalCardLabel: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '600',
    },
    totalCardValue: {
        ...theme.typography.h6,
        color: theme.colors.primary,
        fontWeight: '700',
    },
});

export default AddCowRecordScreen;
